import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0308 from "../lesson-08/lesson";
import lesson0309 from "../lesson-09/lesson";
import lesson0310 from "../lesson-10/lesson";
import lesson0326 from "../lesson-26/lesson";

const linkLesson0308 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0308.id);
const linkLesson0309 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0309.id);
const linkLesson0310 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0310.id);
const linkLesson0326 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0326.id);

/* eslint-disable */
const theory = `
<h3 class=sectionHead>Introduction</h3>
<p>To simulate sound propagation from a sound source to a receiver, it is important to understand the fundamentals of acoustical propagation (in air), and how it is influenced by rooms or, for outdoor situations, by obstacles such as barriers or buildings. The behaviour of sound waves can be described with differential equations such as the homogeneous wave equation. As solving this equation in a computer program for complex environments requires substantial computational effort, a simpler model was proposed: By modelling sound waves as particles or as rays, it is possible to describe and calculate sound fields in rooms or in outdoor environments more efficiently [1, 3].<br class=newline>
<h3 class=sectionHead>Concept</h3>
<p>
  When sound propagates in rooms, the waves that arrive at a listener consist of the direct sound as well as of the later arriving reflections, which have been reflected one or multiple times at the walls and surfaces of the room. The direct sound of a typical sound source (such as a loudspeaker) is radiated spherically. The propagation of direct sound is influenced by air absorption and has no interaction with the room. If it is not blocked by an obstacle, the direct sound will be the first wave front to arrive at the listener, and usually the loudest one as well. For sound sources radiating spherical waves (point sources), the sound intensity decreases by
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mfrac>
      <mrow>
        <mn>1</mn>
      </mrow>
      <mrow>
        <msup>
          <mrow>
            <mi>r</mi>
          </mrow>
          <mrow>
            <mn>2</mn>
          </mrow>
        </msup>
      </mrow>
    </mfrac>
  </math>
  and the sound pressure decreases by
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mfrac>
      <mrow>
        <mn>1</mn>
      </mrow>
      <mrow>
        <mi>r</mi>
      </mrow>
    </mfrac>
  </math>
  .<br class=newline>As for the audible frequency range, spherically propagating waves observed from a distance of about two meters to the sound source have almost the same properties as plane waves, reflection on walls can also be easily modelled by using the energetic absorption coefficient
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <mi>α</mi>
  </math>
  .
<h3 class=sectionHead>Validity of the geometrical approach</h3>
<p>
  In the geometrical approach sound propagation is modelled by a particle or ray. In the common implementations, wave-based effects such as interference and room modes are neglected. While this has the upside of efficient calculation routines, it leads to severe errors of the simulation model in certain cases, e.g., when room modes play a role (below the Schroeder-frequency
  <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
    <msub>
      <mrow>
        <mi>f</mi>
      </mrow>
      <mrow>
        <mi>S</mi>
      </mrow>
    </msub>
  </math>
   [2]) or when the sound source and/or the receiver is located close the room boundaries. In this case the plane wave assumption for the reflection modelling is not correct anymore. A qualitative example of this error for five frequencies and two room sizes is presented in the principle part of this lesson. Here it is assumed that an error of less than one 1 dB for the simulated sound pressure level is acceptable.<br class=newline>Besides the wave-based difficulties in the geometric approach, there are other general challenges for GA-based simulation models, such as accuracy of the geometric model, the material database accuracy and the source description. These challenges are discussed in more detail in <strong><a href="${linkLesson0326}">Lesson 3.26</a></strong>.
<h3 class=sectionHead>Overview of GA-based models</h3>
<p>All these methods have their own advantages and disadvantages. In the following we will give a brief introduction to the most common geometrical methods in acoustics.
<ul class=itemize1>
  <li class=itemize><strong><span class=cmbx-10>The image source model</span></strong> is based on the concept that you can replace a reflecting surface by an image source. In this way you can easily calculate specular early reflections. While the model is fairly easy to implement, the number of image sources grows exponentially, which makes it inefficient for higher reflection orders. The image source model will be presented in <strong><a href="${linkLesson0308}">Lesson 3.8</a></strong>.
  <li class=itemize><strong><span class=cmbx-10>Ray tracing algorithms</span></strong> model that a source emits a finite number of rays in all directions. Each ray will be traced in a room and will loose energy according to the travelled time and reflections on walls. All rays which intersect with a detection sphere around the receiver are included in a resulting energy decay curve. The accuracy of the method depends on the number of rays that can also become very large. A more refined technique is beam tracing in which the ray is not modelled as a simple line but as a beam, which is more realistic from the wave-front point of view. Ray tracing methods will be the object of <strong><a href="${linkLesson0309}">Lesson 3.9</a></strong>.
  <li class=itemize><strong><span class=cmbx-10>The radiosity method</span></strong> is an energy method that assumes ideal diffuse reflections. This method involves modelling the surface of the area with a mesh, similar to procedures in wave-based simulation models (e.g., BEM). Form factors between all pairs of elements of the mesh are pre-computed. The sound energy is then propagated from the source to all the surface elements and then iteratively dispatched between them. One approach to extend the radiosity method is the Acoustic Radiance Transfer method that overcomes the limit of diffuse reflection by introducing form-factors that depends on the direction of the wave. These methods will be the object of <strong><a href="${linkLesson0310}">Lesson 3.10</a></strong>.
</ul>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
  <dt class=thebibliography id=X0-Kuttruff2009>[1]
  <dd class=thebibliography id=bib-1>
    <p>Heinrich Kuttruff. <span class=cmti-10>Room acoustics</span>. 5th. Milton Park, Abingdon, United Kingdom: Spon Press, 2009.
  <dt class=thebibliography id=X0-Schroeder1965>[2]
  <dd class=thebibliography id=bib-2>
    <p>M R Schroeder. “New method of measuring reverberation time”. In: <span class=cmti-10>J. </span><span class=cmti-10>Acoust. Soc. Am. </span>37 (1965), pp. 409–412. <span class=cmcsc-10>
  <dt class=thebibliography id=X0-Vorlander2008>[3]
  <dd class=thebibliography id=bib-3>
    <p>Michael Vorländer. <span class=cmti-10>Auralization. Fundamentals of acoustics, modelling, </span><span class=cmti-10>simulation, algorithms and acoustic virtual reality</span>. 1st. Berlin, Heidelberg, Germany: Springer, 2008.
</dl>


<h3 class=sectionHead>Glossary</h3>
<dl>
  <dt>GA
  <dd>Geometrical Acoustics
  <dt>BEM
  <dd>Boundary Element Method</dd>
</dl>
`;
/* eslint-enable */

export default theory;
